import type { Environment } from "@/types/shared";
import { COPYRIGHT_TEMPLATE, DEFAULT_FETCH_TIMEOUT_MS } from "./constants";

export function getCopyrightText(brandName: string): string {
  const currentYear = new Date().getFullYear();
  return COPYRIGHT_TEMPLATE.replace("[YEAR]", currentYear.toString()).replace(
    "[BRAND_NAME]",
    brandName
  );
}

export const convertHTMLtoData = (
  htmlData: string,
  pattern: RegExp
): string[] | null => {
  const match = htmlData.match(pattern);
  if (match) {
    return match;
  }
  return null;
};

export const convertHTMLtoUrl = (htmlData: string): string[] | null => {
  const regex = /href="\s?(https?:\/\/[^"]+|\/[^"]+)\s?"[^>]*>([^<]+)<\/a>/;
  const match = htmlData.match(regex);
  if (match) {
    return match;
  }
  return null;
};

export const getLinkItem = (
  textData: string | null,
  linkData: string | null
): string[] | null => {
  if (textData) {
    let match = convertHTMLtoUrl(textData);
    if (match) {
      return match;
    }

    match = [];
    match.push("");
    match.push(linkData ? linkData : "");
    match.push(removeAllHTMLTags(textData));
    return match;
  }
  return null;
};

export const removeAllHTMLTags = (htmlData: string): string => {
  if (htmlData) {
    return htmlData.replace(
      /<\w+ \w+=?"[a-z -.]+>|[<[a-z]+ ?[a-z]+=\\"[a-z -.]+\\">([a-z.-/])?|<\/[a-z]+>|<[a-z]?>|<[a-z ]+\/>/g,
      ""
    );
  }
  return htmlData;
};

export const fetchDefaultStore = (siteUid: string): string =>
  siteUid.split("_")[1] || "FR";

interface FetchWithHeadersOptions extends RequestInit {
  timeout?: number;
}

/**
 * Fetch function that includes standard headers and optional timeout.
 * @param url - The URL to fetch.
 * @param init - Additional fetch options including optional timeout in ms.
 * @returns The native fetch Response.
 */
export async function fetchWithHeaders(
  url: string,
  init?: FetchWithHeadersOptions
): Promise<Response> {
  const userAgentHeader = process.env.USER_AGENT_HEADER || "Mozilla/5.0";
  const timeout = init?.timeout ?? DEFAULT_FETCH_TIMEOUT_MS;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const headers = {
    "User-Agent": userAgentHeader,
    ...init?.headers,
  };

  try {
    const response = await fetch(url, {
      ...init,
      headers,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function getEnvVariable(envVarName: string) {
  const value = process.env[envVarName];

  if (!value) {
    throw new Error(`ENV variable ${envVarName} is missing.`);
  }

  return value;
}

export const getCurrentEnvironment = (): Environment =>
  process.env.VERCEL_ENV === "production" ? "PROD" : "TEST";

export function handleFieldErrors<T>(
  fieldErrors: { [key: string]: string[] },
  setError: (field: keyof T, error: { type: string; message: string }) => void
): void {
  Object.entries(fieldErrors).forEach(([fieldName, messages]) => {
    if (messages && messages.length > 0) {
      setError(fieldName as keyof T, {
        type: "server",
        message: messages[0] ?? "Validation error",
      });
    }
  });
}
